<template>
  <div class="tm-form-generator">
    <form
      class="tm-form-container"
      :class="labelPosition == 'top' ? 'tm-form-container-top' : ''"
    >
      <FormItem
        v-for="item in formConfig"
        :key="item.id"
        :formItem="item"
        :labelPosition="labelPosition"
        :labelWidth="labelWidth"
        :value="formData[item.attributes?.prop] || item.attributes?.data || ''"
        :error="fieldErrors[item.attributes?.prop] || ''"
        @input="handleInput"
        @change="handleChange"
        @validate="handleValidation"
        @submit="handleButtonSubmit"
        @click="handleButtonClick"
      />
    </form>
  </div>
</template>

<script>
import "@/assets/fonts/dct-fonts.css";
import FormItem from "./FormItem.vue";
import Schema from "async-validator";
export default {
  name: "tm-form",
  components: {
    FormItem,
  },
  props: {
    // 表单配置
    formConfig: {
      type: Array,
      required: true,
      default: () => [],
    },
    // 表单标签位置
    labelPosition: {
      type: String,
      default: "left",
    },
    labelWidth: {
      type: String,
      default: "120px",
    },
    // 初始表单数据
    initialData: {
      type: Object,
      default: () => ({}),
    },
    // 是否实时验证
    validateOnChange: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      formData: {},
      fieldErrors: {},
      isFormValid: true,
      requestId: "",
    };
  },
  watch: {
    formData: {
      handler(newVal) {
        this.$emit("form-change", newVal);
        if (this.validateOnChange) {
          this.validateForm();
        }
      },
      deep: true,
    },
  },
  created() {
    this.initializeForm();
  },
  methods: {
    // 处理字段输入
    handleInput(fieldName, value) {
      this.$set(this.formData, fieldName, value);
    },

    // 处理字段变化
    handleChange(fieldName, value) {
      this.$set(this.formData, fieldName, value);
    },

    // 处理字段验证
    handleValidation(name, errorMsg) {
      this.$set(this.fieldErrors, name, errorMsg);
      this.isFormValid = Object.values(this.fieldErrors).every((err) => !err);
    },

    // 初始化表单数据
    initializeForm() {
      const initialData = { ...this.initialData };
      this.formConfig.forEach(({ attributes, component }) => {
        if (!attributes.prop) return;
        if (initialData[attributes.prop]) {
          attributes.data = initialData[attributes.prop];
        }
        let data = attributes.data;
        // 如果是 checkbox 组件且 data 是字符串，尝试解析为数组
        if (component === 'tm-checkbox' && typeof data === 'string') {
          try {
            const parsed = JSON.parse(data);
            if (Array.isArray(parsed)) {
              // 将数组中的值转换为字符串，以匹配 options 中的 value 类型
              data = parsed.map(item => String(item));
              attributes.data = data;
            } else {
              data = [];
              attributes.data = [];
            }
          } catch (e) {
            // 如果解析失败，使用空数组
            data = [];
            attributes.data = [];
          }
        } else if (component === 'tm-checkbox' && Array.isArray(data)) {
          // 如果已经是数组，也确保值是字符串类型
          data = data.map(item => String(item));
          attributes.data = data;
        } else if (component === 'tm-checkbox' && !Array.isArray(data)) {
          // 如果既不是字符串也不是数组，使用空数组
          data = [];
          attributes.data = [];
        }
        this.formData[attributes.prop] = data;
      });
    },

    // 规范化校验规则
    normalizeRules(rules) {
      if (!Array.isArray(rules)) return [];
      return rules.map(rule => {
        const normalizedRule = { ...rule };
        // 将字符串 "true" 转换为布尔值 true
        if (normalizedRule.required === "true" || normalizedRule.required === true) {
          normalizedRule.required = true;
        } else if (normalizedRule.required === "false" || normalizedRule.required === false) {
          normalizedRule.required = false;
        }
        // 确保 min 和 max 是数字类型
        if (normalizedRule.min !== undefined && typeof normalizedRule.min === 'string') {
          normalizedRule.min = parseInt(normalizedRule.min);
        }
        if (normalizedRule.max !== undefined && typeof normalizedRule.max === 'string') {
          normalizedRule.max = parseInt(normalizedRule.max);
        }
        return normalizedRule;
      });
    },

    // 校验整个表单
    async validateForm() {
      let isValid = true;
      let errors = {};
      for (const item of this.formConfig) {
        const prop = item.attributes?.prop;
        if (!prop) continue; // 跳过没有 prop 的项（如按钮）
        
        const rules = item.attributes?.rules || [];
        let value = this.formData[prop];
        
        // 如果是 checkbox 组件，确保值是数组类型
        if (item.component === 'tm-checkbox') {
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              value = Array.isArray(parsed) ? parsed.map(item => String(item)) : [];
            } catch (e) {
              value = [];
            }
          } else if (!Array.isArray(value)) {
            value = [];
          }
        }
        
        if (rules && rules.length > 0) {
          // 规范化规则
          const normalizedRules = this.normalizeRules(rules);
          const descriptor = { [prop]: normalizedRules };
          const validator = new Schema(descriptor);
          try {
            await validator.validate({ [prop]: value });
            errors[prop] = "";
          } catch (e) {
            isValid = false;
            errors[prop] =
              e.errors && e.errors.length > 0
                ? e.errors[0].message
                : "字段校验失败";
          }
        } else {
          errors[prop] = "";
        }
      }
      this.fieldErrors = errors;
      this.isFormValid = isValid;
      return isValid;
    },
    handleButtonSubmit(r) {
      this.requestId = r;
      // 触发表单提交和校验（只通过自定义事件，避免重复提交）
      this.handleSubmit();
    },
    // 提交表单
    async handleSubmit() {
      const valid = await this.validateForm();
      if (valid) {
        this.$emit("form-submit", {
          formData: this.formData,
          requestId: this.requestId,
        });
      } else {
        this.$emit("form-error", this.fieldErrors);
      }
    },

    handleButtonClick(requestId) {
      console.log(requestId);
    },

    // 重置表单
    resetForm() {
      this.formData = {};
      this.fieldErrors = {};
      this.initializeForm();
      this.$emit("form-reset");
    },

    // 获取表单数据
    getFormData() {
      return this.formData;
    },

    // 设置表单数据
    setFormData(data) {
      this.formData = { ...this.formData, ...data };
    },

    // 清除错误信息
    clearErrors() {
      this.fieldErrors = {};
    },
  },
};
</script>

<style scoped>
.tm-form-generator {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.tm-form-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.tm-form-container-top .tm-form-item {
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 30px;
}
</style>
